import { navigationLinks } from 'src/app/sidenav/navigationLinks';

/** A dashboard product the client can pick in a request. `key` is what is sent. */
export interface RequestProduct {
  key: string;
  /** A `sidenav_links` translation key. */
  titleKey: string;
  /** Route prefixes that preselect it. */
  paths: string[];
}

/** Products the sidenav lists under its secondary links, not the main ones. */
const SECONDARY_PRODUCTS = ['Integration', 'Website Analysis'];

/** Pages outside a product's own routes that still belong to it. */
const EXTRA_PATHS: Record<string, string[]> = {
  Nudge: ['/nudge-detailed', '/nudge-builder'],
  Signals: ['/signal'],
};

/**
 * The sidenav's products the user can open (same privilege rules as the
 * sidenav), plus the ones that live in the secondary links.
 */
export function requestProducts(privileges: string[]): RequestProduct[] {
  const has = (privilege: string) =>
    !!privilege && privileges.includes(privilege);
  const products: RequestProduct[] = [];

  for (const link of navigationLinks as any[]) {
    if (link.hideFromView && !SECONDARY_PRODUCTS.includes(link.title)) continue;
    const children: any[] = link.children || [];
    const allowed = children.length
      ? children.some((child) => has(child.privilege))
      : has(link.privilege);
    if (!allowed) continue;

    const paths = [
      link.routerLink,
      ...children.map((child) => child.routerLink),
    ]
      .filter(Boolean)
      .map(firstSegment);
    products.push({
      key: link.title,
      titleKey: `sidenav_links.${link.title}`,
      paths: [...new Set([...paths, ...(EXTRA_PATHS[link.title] || [])])],
    });
  }
  return products;
}

/** The product whose pages the user is on, if any. */
export function productForUrl(
  products: RequestProduct[],
  url: string,
): string | null {
  const path = url.split(/[?#]/)[0];
  const match = products.find((product) =>
    product.paths.some((p) => path === p || path.startsWith(p + '/')),
  );
  return match?.key || null;
}

/** `/seo/content` → `/seo`, so a product's sub-pages all match. */
function firstSegment(route: string): string {
  return '/' + route.replace(/^\//, '').split('/')[0];
}
